// src/components/ProfileModal.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';
import { useAuth, UserProfileData } from '../AuthProvider';
import { supabase } from '../utils/supabaseClient';
import { updateUserProfile } from '../API'; // Import the API function
import config from '../config';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

const ProfileModal = ({ open, onClose }: ProfileModalProps) => {
  const { profile, refreshProfile, loading: authLoading, user } = useAuth();
  const [displayName, setDisplayName] = useState<string>('');
  const [showOnLeaderboards, setShowOnLeaderboards] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || '');
      setShowOnLeaderboards(profile.show_on_leaderboards);
    } else if (user && !authLoading) {
      // If profile is null but user exists and not loading, might be first time
      // or a fetch issue. For now, set defaults.
      setDisplayName('');
      setShowOnLeaderboards(true);
    }
  }, [profile, user, authLoading]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!user) {
        setError("You must be logged in to update your profile.");
        setIsLoading(false);
        return;
    }
    
    try {
      const response = await updateUserProfile(displayName, showOnLeaderboards);
      const data = response.data;

      if (response.status === 200 && data.status === 'success') {
        setSuccessMessage('Profile updated successfully!');
        await refreshProfile(); // Refresh context profile
        setTimeout(() => { // Optional: close modal after success
            onClose();
            setSuccessMessage('');
        }, 2000);
      } else {
        setError(data.message || data.error || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || !profile && user) { // Show loading if auth is loading or profile is being fetched for a user
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!user || !profile) { // Should ideally not happen if modal is opened only for logged-in users
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>My Account</DialogTitle>
        <DialogContent>
          <Typography>Please log in to view your profile.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>My Account</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
          
          <Typography variant="h6" gutterBottom>Profile Details</Typography>
          <Typography variant="body1">Email: {user.email}</Typography>
          <Typography variant="body1">Joined: {new Date(profile.date_joined).toLocaleDateString()}</Typography>
          
          <Box sx={{ my: 3 }}>
            <Typography variant="h6" gutterBottom>Statistics</Typography>
            <Typography variant="body1">Gardens Submitted: {profile.gardens_submitted}</Typography>
            <Typography variant="body1">Total Points: {profile.total_points}</Typography>
          </Box>

          <TextField
            label="Display Name (for Leaderboards)"
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            margin="normal"
            helperText="Leave blank to use a default name."
            disabled={isLoading}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showOnLeaderboards}
                onChange={(e) => setShowOnLeaderboards(e.target.checked)}
                disabled={isLoading}
              />
            }
            label="Show my stats on public leaderboards"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfileModal;